import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import cookie from "react-cookies";
import { getAcademyHome, getAcademyMe, getAcademyCertificates, getLiveShiftInvites } from "../../services/academyApi";
import {
  TrainingHero,
  TrainingLoading,
  TrainingProgressBar,
  TrainingProgressByProgram,
  countAcademyModules,
  countMandatoryPending,
  countIssuedCertsByProgram,
} from "../academy/AcademyShared";
import { TRAINING, displayProgramTitle } from "../academy/trainingTerms";
import MeLayout from "./MeLayout";

export default function MeProgress() {
  const [home, setHome] = useState(null);
  const [candidate, setCandidate] = useState(null);
  const [issuedCertCounts, setIssuedCertCounts] = useState(new Map());
  const [liveShift, setLiveShift] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!cookie.load("candidateKey")) {
      window.location.href = "/login?next=/me";
      return;
    }
    setLoading(true);
    Promise.all([
      getAcademyHome(),
      getAcademyMe(),
      getAcademyCertificates(),
      getLiveShiftInvites().catch(() => ({ invites: [] })),
    ])
      .then(([homeRes, meRes, certRes, shiftRes]) => {
        setHome(homeRes);
        setCandidate(meRes?.candidate || null);
        setIssuedCertCounts(countIssuedCertsByProgram(certRes?.certificates || []));
        const upcoming = (shiftRes?.invites || []).find((invite) =>
          ["invited", "accepted"].includes(invite.status)
        );
        setLiveShift(upcoming || null);
      })
      .catch(() => {
        setHome(null);
        setCandidate(null);
      })
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <MeLayout title="My progress">
        <TrainingLoading label="Loading your progress..." />
      </MeLayout>
    );
  }

  if (!home) {
    return (
      <MeLayout title="My progress">
        <div className="training-empty">
          <p>Could not load your progress. Try again in a moment.</p>
          <button type="button" className="training-btn-primary" onClick={() => window.location.reload()}>
            Retry
          </button>
        </div>
      </MeLayout>
    );
  }

  const academies = home.academies || [];
  const { total, done } = countAcademyModules(academies);
  const remaining = total - done;
  const mandatoryPending = countMandatoryPending(academies);
  const overallPct = total ? Math.round((done / total) * 100) : 0;
  const displayName = candidate?.firstname || candidate?.email || "Your progress";
  const totalCerts = [...issuedCertCounts.values()].reduce((sum, n) => sum + n, 0);

  return (
    <MeLayout title="My progress">
      <TrainingHero
        eyebrow="Your learning"
        title={displayName}
        subtitle={
          total > 0
            ? `${done} of ${total} published ${TRAINING.modules.toLowerCase()} complete${totalCerts > 0 ? ` · ${totalCerts} certificate${totalCerts === 1 ? "" : "s"} earned` : ""}.`
            : `Track your learning across all ${TRAINING.programs.toLowerCase()}.`
        }
      />

      {!candidate?.profile_completed_at && (
        <Link to="/me/profile" className="training-profile-nudge">
          <strong>Complete your profile</strong>
          <span>Add your details and upload your latest CV so recruiters can match you.</span>
        </Link>
      )}

      {liveShift && (
        <Link to="/me/live-shift" className="training-profile-nudge">
          <strong>
            {liveShift.status === "accepted"
              ? "Your Live Shift Interview is booked"
              : "You are invited to a Live Shift Interview"}
          </strong>
          <span>
            Full day processing customer and driver orders. Earn R
            {Math.round((liveShift.stipend_cents || 50000) / 100)} after you attend.
            RSVP now.
          </span>
        </Link>
      )}

      {total > 0 && (
        <>
          <div className="training-stats">
            <div className="training-stat">
              <span className="training-stat-value">{done}</span>
              <span className="training-stat-label">Modules done</span>
            </div>
            <div className="training-stat">
              <span className="training-stat-value">{remaining}</span>
              <span className="training-stat-label">Remaining</span>
            </div>
            <div className="training-stat">
              <span className="training-stat-value">{mandatoryPending}</span>
              <span className="training-stat-label">Required left</span>
            </div>
          </div>
          <TrainingProgressBar value={overallPct} label="Overall progress" />
        </>
      )}

      {home.continue_module && (() => {
        const continueProgram = academies.find((a) => a.slug === home.continue_module.academy_slug);
        return (
          <Link
            to={`/academy/${home.continue_module.academy_slug}/module/${home.continue_module.slug}`}
            className="training-continue-card"
          >
            <div className="training-continue-label">Continue learning</div>
            <div className="training-continue-title">{home.continue_module.title}</div>
            <div className="training-continue-meta">
              {displayProgramTitle(continueProgram?.title || home.continue_module.academy_slug)}
              {home.continue_module.watch_pct > 0 ? ` · ${home.continue_module.watch_pct}% lessons` : ""}
            </div>
          </Link>
        );
      })()}

      <h3 className="training-section-title">By {TRAINING.program.toLowerCase()}</h3>
      {total === 0 && done === 0 ? (
        <div className="training-empty">
          <p>No {TRAINING.modules.toLowerCase()} started yet.</p>
          <Link to="/academy" className="training-btn-primary" style={{ display: "inline-flex", marginTop: 12, textDecoration: "none" }}>
            Browse {TRAINING.programs.toLowerCase()}
          </Link>
        </div>
      ) : (
        <TrainingProgressByProgram academies={academies} issuedCertCounts={issuedCertCounts} />
      )}

      <Link to="/me/certificates" className="training-btn-secondary" style={{ display: "inline-flex", marginTop: 20, textDecoration: "none" }}>
        View certificates
      </Link>
    </MeLayout>
  );
}
